import * as THREE from 'three';
import { vec3ToLatLon } from '../geo/latlon';
import type { LatLon } from '../geo/types';
import { GLOBE_RADIUS } from '../core/globe';

/**
 * Cast a ray from the camera through a screen-space point and return the
 * lat/lon where it intersects the globe. Returns null if the ray misses
 * the globe (user pointing at empty space).
 */
export function raycastToLatLon(
  camera: THREE.PerspectiveCamera,
  globeGroup: THREE.Group,
  screenX: number,
  screenY: number,
  canvasWidth: number,
  canvasHeight: number,
): LatLon | null {
  // Convert screen coords → normalized device coords (-1 to 1).
  const ndcX = (screenX / canvasWidth) * 2 - 1;
  const ndcY = -((screenY / canvasHeight) * 2 - 1);

  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);

  // Intersect against a unit sphere in WORLD space — but globeGroup is
  // rotated, so we need the sphere at origin before rotation. Create a
  // throwaway sphere mesh at the globe's location, with the globe's
  // rotation applied, then raycast against its geometry.
  //
  // Simpler: intersect with Three.js's `globeGroup.children[0]` which is
  // the rendered sphere. This accounts for rotation automatically.
  const surface = globeGroup.getObjectByName('globe.surface');
  if (!surface) return null;

  const hits = raycaster.intersectObject(surface, false);
  if (hits.length === 0) return null;

  // Convert world-space hit back to object-space (undo globe rotation)
  // so vec3ToLatLon returns the lat/lon of the underlying texture.
  const worldPoint = hits[0].point;
  const localPoint = globeGroup.worldToLocal(worldPoint.clone());

  // Normalize to unit sphere before converting — latLonToVec3 assumes radius 1.
  localPoint.normalize().multiplyScalar(GLOBE_RADIUS);

  return vec3ToLatLon(localPoint);
}
